import { useState } from "react";

/**
 * Gates any action behind a confirmation dialog — used to satisfy "every
 * save/create/edit button should ask for confirmation first" without
 * duplicating the pending-action state machine in every form. Usage:
 *
 *   const confirmGate = useConfirmGate();
 *   <form onSubmit={(e) => { e.preventDefault(); confirmGate.request(() => mutation.mutate()); }}>
 *   <ConfirmDialog {...confirmGate.dialogProps} message="Save these changes?" />
 */
export function useConfirmGate(): {
  open: boolean;
  request: (action: () => void) => void;
  dialogProps: { open: boolean; onConfirm: () => void; onCancel: () => void };
} {
  const [pending, setPending] = useState<(() => void) | null>(null);

  function request(action: () => void): void {
    setPending(() => action);
  }
  function confirm(): void {
    pending?.();
    setPending(null);
  }
  function cancel(): void {
    setPending(null);
  }

  return {
    open: pending !== null,
    request,
    dialogProps: { open: pending !== null, onConfirm: confirm, onCancel: cancel },
  };
}
